import React, { useState, useEffect, useRef } from 'react';
import ECharts, { EChartsReactProps } from 'echarts-for-react';
import { Link, useNavigate } from 'react-router-dom';
import { typeSex, calculateAge, gapTime, isOccupiedBySense } from '../lib/Common';
import moment from 'moment';
import { GetListLast, GetUsage } from '../lib/DeviceData';
import { GetPredict } from '../lib/Device';
import useClientStore from '../stores/client';
import useWarnningStore from '../stores/warnning';
import { useTranslation } from 'react-i18next';
import i18n from '../stores/i18n';
import Pako from 'pako';

const lineColor = ['#F75C5D', '#3889ff', '#3fd3d4'];


export const deviceState = {
    OFF: 'off',
    NORMAL: 'normal',
    WARNNING: 'warnning',
    DANGER: 'danger',
};

const emptyData = {
    tooltip: {
        show: 'false',
    },
    xAxis: {
        show: false,
        boundaryGap: false,
        data: [],
    },
    yAxis: {
        show: false,
        type: 'value',
    },
    grid: {
        top: 10,
        left: 0,
        right: 0,
        bottom: 0,
    },
    series: [
        {
            name: 'BPM',
            type: 'line',
            smooth: true,
            symbol: 'none',
            lineStyle: {
                color: '#7E7E7E',
                width: 3,
            },
            data: [],
        },
    ],
};

const MonitoringRealtimeCell = props => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { show, addPopups } = useWarnningStore();

    // const [client, setClient] = useState();
    const { client, addOnMessage, removeOnMessage } = useClientStore();

    // 심박수
    const [options1, setOptions1] = useState([]);
    const [value1, setValue1] = useState(0);

    // 호흡수
    const [options2, setOptions2] = useState([]);
    const [value2, setValue2] = useState(0);

    // 무호흡
    const [Apneas, setApneas] = useState([]);
    const [isApnea, setisApnea] = useState(0);

    // 산소불포화 / FSR
    const [options3, setOptions3] = useState([]);
    const [value3, setValue3] = useState(0);
    // 움직임 감지
    const [motion_detected, setMotionDetected] = useState(0);

    const [aiScore, setAiScore] = useState(null);
    const [aiRisk, setAiRisk] = useState(null);
    const [aiUnavailable, setAiUnavailable] = useState(false);
    const [usage, setUsage] = useState(null);
    const [usageTick, setUsageTick] = useState(0);

    const topicRef = useRef('');
    const procDataRef = useRef(null);
    const lastMqttAtRef = useRef(0);
    const isUseRef = useRef(false);
    const seriesHasDataRef = useRef(false);
    const predictFrozenRef = useRef(false);

    const SENSE_POLL_MS = 2000;
    const SENSE_SERIES_MAX = 13;
    const MQTT_FRESH_MS = 15000;

    const toSenseArray = row => {
        if (!row) return null;
        if (Array.isArray(row)) return row;
        if (Array.isArray(row.e)) return row.e;
        const heart = Number(row.heart ?? row.hr ?? row.hr_avg ?? 0);
        const resp = Number(row.resp ?? row.rr ?? row.rr_avg ?? 0);
        const apnea = Number(row.apnea ?? 0);
        const state = Number(row.state ?? 0);
        const fsr = Number(row.fsr_sum ?? row.fsr ?? 0);
        const motion = Number(row.motion_detected ?? row.motion ?? 0);
        return [heart, resp, apnea, state, fsr, 0, 0, 0, 0, 0, 0, motion];
    };

    const normalizeSenseRows = res => {
        if (!res) return [];
        if (Array.isArray(res)) return res;
        if (Array.isArray(res.rows)) return [...res.rows].reverse();
        if (Array.isArray(res.e) || res.heart != null || res.hr != null || res.fsr != null || res.fsr_sum != null) {
            return [res];
        }
        return [];
    };

    const rowHeart = row => {
        if (Array.isArray(row?.e)) return Number(row.e[0] ?? 0);
        return Number(row?.heart ?? row?.hr ?? row?.hr_avg ?? 0);
    };
    const rowResp = row => {
        if (Array.isArray(row?.e)) return Number(row.e[1] ?? 0);
        return Number(row?.resp ?? row?.rr ?? row?.rr_avg ?? 0);
    };
    const rowFsr = row => {
        if (Array.isArray(row?.e)) return Number(row.e[4] ?? 0);
        return Number(row?.fsr_sum ?? row?.fsr ?? 0);
    };

    const reloadSenseSeries = rows => {
        const sliced = rows.slice(-SENSE_SERIES_MAX);
        setOptions1(sliced.map(rowHeart));
        setOptions2(sliced.map(rowResp));
        setOptions3(sliced.map(rowFsr));
        if (sliced.length) seriesHasDataRef.current = true;
    };

    const decodeHrvPayload = message => {
        const raw = typeof message === 'string' ? message : message?.toString?.() ?? '';
        try {
            const compressed = Uint8Array.from(atob(raw), c => c.charCodeAt(0));
            return JSON.parse(Pako.inflate(compressed, { to: 'string' }));
        } catch (_) {
            try {
                return JSON.parse(Pako.inflate(message, { to: 'string' }));
            } catch (e2) {
                try {
                    return JSON.parse(raw);
                } catch {
                    return null;
                }
            }
        }
    };

    // 상세와 동일: MQTT aw/{sn}/stream/hrv 바이탈로 카드/미니그래프 갱신
    useEffect(() => {
        const sn = props.data?.device_sn;
        if (!sn) return;

        const topic = `aw/${sn}/stream/hrv`;
        topicRef.current = topic;

        const onMessage = (msgTopic, message) => {
            if (topicRef.current !== msgTopic) return;
            const parsed = decodeHrvPayload(message);
            if (!parsed) return;
            const body = parsed.timestamp != null ? parsed.data : parsed;
            if (!body) return;

            lastMqttAtRef.current = Date.now();
            const sense = [
                Math.round(Number(body.heart_rate) || 0),
                Math.round(Number(body.resp_rate) || 0),
                Number(body.apnea_detected) || 0,
                0,
                Number(body.fsr_sum) || 0,
                0,
                0,
                0,
                0,
                0,
                0,
                Number(body.motion_detected) || 0,
            ];
            if (procDataRef.current) procDataRef.current(sense);
        };

        if (client) {
            client.subscribe(topic, () => {});
        }
        addOnMessage(topic, onMessage);

        return () => {
            if (client) {
                client.unsubscribe(topic, () => {});
            }
            removeOnMessage(topic);
        };
    }, [props.data?.device_sn, client]);

    // MQTT 없을 때만 last API 폴백
    useEffect(() => {
        const deviceNo = props.data?.device_no;
        if (!deviceNo) return;
        seriesHasDataRef.current = false;

        const fetchSense = () => {
            const mqttFresh = Date.now() - lastMqttAtRef.current < MQTT_FRESH_MS;
            if (mqttFresh && seriesHasDataRef.current) return;

            GetListLast(
                { id: deviceNo, device_no: deviceNo },
                res => {
                    const mqttFreshNow = Date.now() - lastMqttAtRef.current < MQTT_FRESH_MS;
                    if (mqttFreshNow && seriesHasDataRef.current) return;
                    const rows = normalizeSenseRows(res);
                    if (!rows.length) return;

                    const latest = rows[rows.length - 1];
                    const sense = toSenseArray(latest);
                    const inUse = sense ? isOccupiedBySense(sense[4], sense[11]) : false;
                    if (inUse || !seriesHasDataRef.current) {
                        reloadSenseSeries(rows);
                    }
                    if (sense && procDataRef.current) {
                        procDataRef.current(sense, { skipSeries: true });
                    }
                },
                err => {
                    console.log('deviceData/last poll error', err?.message || err);
                },
            );
        };

        fetchSense();
        const timer = setInterval(fetchSense, SENSE_POLL_MS);
        return () => clearInterval(timer);
    }, [props.data?.device_no]);

    useEffect(() => {
        const deviceNo = props.data?.device_no;
        if (!deviceNo) return;

        const fetchUsage = () => {
            GetUsage({ device_no: deviceNo, id: deviceNo }, res => setUsage(res));
        };
        fetchUsage();
        const timer = setInterval(fetchUsage, 10 * 1000);
        return () => clearInterval(timer);
    }, [props.data?.device_no]);

    useEffect(() => {
        if (!usage?.in_use) return;
        const timer = setInterval(() => setUsageTick(n => n + 1), 1000);
        return () => clearInterval(timer);
    }, [usage?.in_use, usage?.session_started_at]);

    const classifyRisk = score => {
        if (score == null || Number.isNaN(score)) return null;
        if (score <= 60) return 'safe';
        if (score < 80) return 'caution';
        return 'danger';
    };

    const riskLabel = {
        safe: t('안전'),
        caution: t('주의'),
        danger: t('위험'),
    };

    useEffect(() => {
        const deviceNo = props.data?.device_no;
        if (!deviceNo) return;
        predictFrozenRef.current = false;
        if (props.data?.device_info && props.data.device_info !== 'Y') {
            setAiScore(null);
            setAiRisk(null);
            setAiUnavailable(false);
            return;
        }

        const applyPredict = res => {
            const unavailable =
                res?.ready === false ||
                res?.label === '데이터 확인 불가' ||
                (typeof res?.message === 'string' && res.message.includes('확인할 수 없습니다'));

            if (unavailable) {
                setAiScore(null);
                setAiRisk(null);
                setAiUnavailable(true);
                return;
            }

            const latest = Array.isArray(res?.rows)
                ? res.rows[0]
                : res?.score != null || res?.result != null
                  ? res
                  : null;
            const scoreSrc = res?.score != null ? res : latest;
            const score =
                scoreSrc?.score != null
                    ? Number(scoreSrc.score)
                    : scoreSrc?.result != null
                      ? Number(scoreSrc.result) * 100
                      : null;
            setAiUnavailable(false);
            setAiScore(score);
            setAiRisk(classifyRisk(score));
        };

        const fetchPredict = () => {
            if (!isUseRef.current && predictFrozenRef.current) return;
            GetPredict(deviceNo, 60, res => {
                applyPredict(res);
                if (!isUseRef.current) predictFrozenRef.current = true;
            }, err => {
                const body = err?.response?.data;
                if (body && (body.ready === false || body.label === '데이터 확인 불가')) {
                    applyPredict(body);
                    if (!isUseRef.current) predictFrozenRef.current = true;
                    return;
                }
                setAiScore(null);
                setAiRisk(null);
                setAiUnavailable(false);
                if (!isUseRef.current) predictFrozenRef.current = true;
            });
        };

        fetchPredict();
        const timer = setInterval(fetchPredict, 60 * 1000);
        return () => clearInterval(timer);
    }, [props.data?.device_no, props.data?.device_info]);

    useEffect(() => {
        let timer = setInterval(() => {
            if (props.data.online) {
                // 30초 동안 신호가 없을때 비활성화
                const sec = moment.duration(moment().diff(props.data.recieveTime)).asSeconds();
                if (sec > 30) {
                    props.data.state = deviceState.OFF;
                    props.data.online = false;
                    props.onChange(props.data);
                    setValue1(0);
                    setValue2(0);
                    setValue3(0);
                    setMotionDetected(0);
                }
            }
        }, 1000);

        return () => clearInterval(timer);
    });

    const procData = (data, options = {}) => {
        const skipSeries = !!options.skipSeries;
        const heart = data[0];
        const resp = data[1];
        const apnea = data[2];
        const state = data[3];
        const fsr_sum = data[4];
        var motion_detected = 0;

        if (data.length >= 11) {
            motion_detected = data[11];
            props.data.motion_detected = motion_detected;
            setMotionDetected(motion_detected);
        }

        if (!props.data.online) {
            props.data.online = true;
            props.data.state = deviceState.NORMAL;
            props.data.connectTime = moment();

            const heart_ranges = props.data.heart_range?.split('~');
            if (heart_ranges?.length >= 2) {
                props.data.heart_range_from = heart_ranges[0];
                props.data.heart_range_to = heart_ranges[1];
            } else {
                props.data.heart_range_from = 60;
                props.data.heart_range_to = 100;
            }

            const resp_ranges = props.data.resp_range?.split('~');
            if (resp_ranges?.length >= 2) {
                props.data.resp_range_from = resp_ranges[0];
                props.data.resp_range_to = resp_ranges[1];
            } else {
                props.data.resp_range_from = 10;
                props.data.resp_range_to = 20;
            }

            props.onChange(props.data);
        }

        const oldHeart = props.data.warnning_heart;
        const oldResp = props.data.warnning_resp;
        props.data.warnning_heart = heart < props.data.heart_range_from || heart > props.data.heart_range_to;
        props.data.warnning_resp = resp < props.data.resp_range_from || resp > props.data.resp_range_to;
        props.data.recieveTime = moment();

        // 사용/미사용: 압력 임계값 또는 움직임. 움직임은 미사용이 아님
        const measuredHr = motion_detected ? 0 : heart;
        const measuredRr = motion_detected ? 0 : resp;
        const isUse = isOccupiedBySense(fsr_sum, motion_detected);
        isUseRef.current = isUse;
        if (isUse) predictFrozenRef.current = false;
        if (isUse) {
            const oldState = props.data.state;
            if (!props.data.warnning_heart && !props.data.warnning_resp) {
                props.data.state = deviceState.NORMAL;
            } else if (props.data.warnning_heart && props.data.warnning_resp) {
                props.data.state = deviceState.DANGER;
            } else {
                props.data.state = deviceState.WARNNING;
            }

            if (oldState !== props.data.state) {
                props.onChange(props.data);
            }

            if ((props.data.warnning_heart && !oldHeart) || (props.data.warnning_resp && !oldResp)) {
                if (show) addPopups(props.data.user.member_no);
            }
        } else {
            props.data.state = deviceState.OFF;
            props.data.warnning_heart = false;
            props.data.warnning_resp = false;
        }

        setValue1(measuredHr);
        setValue2(measuredRr);
        setValue3(fsr_sum);
        setMotionDetected(motion_detected);
        setisApnea(apnea);

        if (!skipSeries && isUse) {
            options1.push(measuredHr);
            options2.push(measuredRr);
            options3.push(fsr_sum);
            Apneas.push(apnea);
            setApneas([...Apneas.slice(-13)]);
            setOptions1([...options1.slice(-13)]);
            setOptions2([...options2.slice(-13)]);
            setOptions3([...options3.slice(-13)]);
            seriesHasDataRef.current = true;
        }
    };

    procDataRef.current = procData;

    const getOption = index => {
        return {
            tooltip: {
                show: 'false',
            },
            xAxis: {
                show: false,
                boundaryGap: false,
                data: [
                    `${t('오전')}\n1${t('시')}`,
                    `${t('오전')}\n2${t('시')}`,
                    `${t('오전')}\n3${t('시')}`,
                    `${t('오전')}\n4${t('시')}`,
                    `${t('오전')}\n5${t('시')}`,
                    `${t('오전')}\n6${t('시')}`,
                    `${t('오전')}\n7${t('시')}`,
                    `${t('오전')}\n8${t('시')}`,
                    `${t('오전')}\n9${t('시')}`,
                    `${t('오전')}\n10${t('시')}`,
                    `${t('오전')}\n11${t('시')}`,
                    `${t('오전')}\n12${t('시')}`,
                    `${t('오후')}\n1${t('시')}`,
                ],
            },
            yAxis: {
                show: false,
                type: 'value',
            },
            grid: {
                top: 10,
                left: 0,
                right: 0,
                bottom: 0,
            },
            series: [
                {
                    name: 'BPM',
                    type: 'line',
                    smooth: true,
                    symbol: 'none',
                    lineStyle: {
                        color: props.data.online ? motion_detected === 1? '#CCCCCC' :lineColor[index] : '#7E7E7E',
                        width: 3,
                    },
                    data: index === 1 ? options1 : index === 2 ? options2 : options3,
                },
            ],
        };
    };

    // <article className="real_time_graph">
    // <article className="real_time_graph off">
    // <article className="real_time_graph click">
    var username="";

    if(i18n.resolvedLanguage==="en"){
        username=props.data.user?.name_eng;
    }else{
        username=props.data.user?.name;
    }

    // 압력 임계값 또는 움직임이면 사용 중. 미사용 시에도 마지막 그래프는 유지
    const isUse = isOccupiedBySense(value3, motion_detected);
    const isApnea_v=isApnea;
    const usageClock = !usage?.in_use
        ? null
        : usage.session_started_at && moment(usage.session_started_at).isValid()
          ? gapTime(usage.session_started_at)
          : usage.usage_hm || null;

    return (
        <li onClick={props.onClick} style={{ cursor: 'pointer' }}>
            <article className={props.data.online ? 'real_time_graph' : 'real_time_graph off'}>
                <div className="tops">
                    <span className="icon wifi"></span>
                    <span className="icon sleep" style={props.data.online && isUse ? { backgroundColor: '#FF5B45' } : {}}></span>
                    {!props.data.online && <p className="alert">{t('없음')}</p>}
                    <p className="info">
                        <b>{props.data.user?.member_no}</b>{' '}
                        {`${props.data.user?.name ? username : t('이름없음')} [${calculateAge(props.data.user?.birth)}${t('세')}${
                            props.data.user?.sex ? '/' + t(typeSex[props.data.user?.sex]) : ''
                        }]`}
                    </p>
                    {props.data.online && usageClock && (
                        <p className="time" key={usageTick}>{usageClock}</p>
                    )}
                    {!isUse ? (
                        <span className="risk_badge unavailable" title="미사용 · 예측 중지">
                            {t('예측 중지')}
                        </span>
                    ) : aiUnavailable ? (
                        <span className="risk_badge unavailable" title="데이터 부족으로 인한 예측 불가">
                            {t('예측 불가')}
                        </span>
                    ) : aiRisk ? (
                        <span className={`risk_badge ${aiRisk}`} title={t('심정지 위험도')}>
                            {t('심정지')} {riskLabel[aiRisk]}
                            {aiScore != null ? ` ${Math.round(aiScore)}` : ''}
                        </span>
                    ) : null}
                </div>
                <div className="graph_box">
                    {/* <div className="wide heart01">
                        <div className="sbj_box">
                            <p className="sbj heart">{t('심박변이도')}</p>
                        </div>
                        <div className="graphs">
                            <ECharts
                                option={getOption(3)}
                                notMerge={true}
                                lazyUpdate={true}
                                opts={{ renderer: 'svg', width: 'auto', height: '60px' }}
                                style={{ height: '60px' }}
                            />
                        </div>
                    </div> */}
                    <div className="wide">
                        <div className={'sbj_box' + (isUse && props.data.warnning_heart ? ' on' : '')}>
                            <p className="sbj heart">{t('심박수')}</p>
                            <p className="number heart">{props.data.online ? (isUse ? value1 : 'NaN') : 'NaN'}</p>
                        </div>
                        <div className="graphs">
                            <ECharts
                                option={options1.length ? getOption(1) : emptyData}
                                notMerge={true}
                                lazyUpdate={true}
                                opts={{ renderer: 'svg', width: 'auto', height: '60px' }}
                                style={{ height: '60px' }}
                            />
                        </div>
                    </div>
                    <div className="half">
                        <div className={'sbj_box' + (isUse && props.data.warnning_resp ? ' on' : '')}>
                            <p className="sbj breath">{t('호흡수')}</p>
                            <p className="number breath">{props.data.online ? (isUse ? (isApnea_v==1 ? t('무호흡') : value2) : 'NaN') : 'NaN'}</p>
                        </div>
                        <div className="graphs">
                            <ECharts
                                option={options2.length ? getOption(2) : emptyData}
                                notMerge={true}
                                lazyUpdate={true}
                                opts={{ renderer: 'svg', width: 'auto', height: '60px' }}
                                style={{ height: '60px' }}
                            />
                        </div>
                    </div>
                    <div className="half">
                        <div className="sbj_box">
                            <p className="sbj fsr">{t('사용여부')}</p>
                            {/* <p className="number oxygen">{value3}</p> */}
                        </div>
                        {/* <div className="graphs">
                            <ECharts
                                option={getOption(3)}
                                notMerge={true}
                                lazyUpdate={true}
                                opts={{ renderer: 'svg', width: 'auto', height: '60px' }}
                                style={{ height: '60px' }}
                            />
                        </div> */}
                        <div className="graphs">
                            <p className="text">{props.data.online ? (isUse ? motion_detected === 1? t("움직임 감지") :t('사용 중') : t('미사용')) : ''}</p>
                            <p className="subtext">{`(${t('압력값')} : ${props.data.online ? value3 : 0})`}</p>
                        </div>
                    </div>
                </div>
            </article>
        </li>
    );
};

export default MonitoringRealtimeCell;
